import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "prevBtn", "nextBtn", "fadeLeft", "fadeRight", "fadeContainer"]

  connect() {
    this.isDragging = false
    this.startX = 0
    this.startY = 0
    this.scrollLeft = 0
    this.cardWidth = this.cardWidthCalc()
    this.axisLocked = null
    this.fadeTicking = false
    this.positionFades = this.positionFades.bind(this)
    this.updateFadeVisibility = this.updateFadeVisibility.bind(this)
    this.onWindowScroll = this.onWindowScroll.bind(this)
    this.trackTarget.addEventListener("scroll", this.updateFadeVisibility)
    window.addEventListener("resize", this.positionFades)
    window.addEventListener("scroll", this.onWindowScroll, { passive: true })
    this.positionFades()
    this.updateFadeVisibility()
  }

  disconnect() {
    this.removeDragListeners()
    this.trackTarget.removeEventListener("scroll", this.updateFadeVisibility)
    window.removeEventListener("resize", this.positionFades)
    window.removeEventListener("scroll", this.onWindowScroll)
  }

  onWindowScroll() {
    if (this.fadeTicking) return
    this.fadeTicking = true
    requestAnimationFrame(() => {
      this.positionFades()
      this.fadeTicking = false
    })
  }

  positionFades() {
    const track = this.trackTarget
    if (!track) return

    const rect = track.getBoundingClientRect()
    const top = Math.max(0, rect.top)
    const bottom = Math.min(window.innerHeight, rect.bottom)
    const height = bottom - top

    const fadeWidth = Math.max(40, Math.min(140, Math.round(window.innerWidth * 0.15)))
    const fadeGradientRight = `linear-gradient(to right, #020202 0%, #020202 30%, rgba(2,2,2,0.85) 50%, rgba(2,2,2,0.5) 70%, rgba(2,2,2,0.15) 90%, transparent 100%)`
    const fadeGradientLeft = `linear-gradient(to left, #020202 0%, #020202 30%, rgba(2,2,2,0.85) 50%, rgba(2,2,2,0.5) 70%, rgba(2,2,2,0.15) 90%, transparent 100%)`

    if (this.hasFadeLeftTarget) {
      this.fadeLeftTarget.style.width = `${fadeWidth}px`
      this.fadeLeftTarget.style.height = `${height}px`
      this.fadeLeftTarget.style.top = `${top}px`
      this.fadeLeftTarget.style.background = fadeGradientRight
    }

    if (this.hasFadeRightTarget) {
      this.fadeRightTarget.style.width = `${fadeWidth}px`
      this.fadeRightTarget.style.height = `${height}px`
      this.fadeRightTarget.style.top = `${top}px`
      this.fadeRightTarget.style.background = fadeGradientLeft
    }

    this.updateFadeVisibility()
  }

  updateFadeVisibility() {
    const track = this.trackTarget
    if (!track) return
    const maxScroll = track.scrollWidth - track.clientWidth
    const scrolled = track.scrollLeft

    const rect = track.getBoundingClientRect()
    const inViewport = rect.bottom > 0 && rect.top < window.innerHeight

    const showLeft = inViewport && scrolled > 2
    const showRight = inViewport && scrolled < maxScroll - 2

    if (this.hasFadeLeftTarget) {
      if (showLeft) {
        this.fadeLeftTarget.classList.remove("opacity-0")
        this.fadeLeftTarget.classList.add("opacity-100")
      } else {
        this.fadeLeftTarget.classList.add("opacity-0")
        this.fadeLeftTarget.classList.remove("opacity-100")
      }
    }

    if (this.hasFadeRightTarget) {
      if (showRight) {
        this.fadeRightTarget.classList.remove("opacity-0")
        this.fadeRightTarget.classList.add("opacity-100")
      } else {
        this.fadeRightTarget.classList.add("opacity-0")
        this.fadeRightTarget.classList.remove("opacity-100")
      }
    }
  }

  cardWidthCalc() {
    const track = this.trackTarget
    if (!track) return 320
    const firstCard = track.querySelector("[data-calendario-card]")
    if (!firstCard) return 320
    const gap = parseInt(getComputedStyle(track).gap) || 16
    return firstCard.offsetWidth + gap
  }

  next() {
    const track = this.trackTarget
    if (!track) return
    this.cardWidth = this.cardWidthCalc()
    const step = this.getItemsPerView()
    track.scrollBy({ left: this.cardWidth * step, behavior: "smooth" })
  }

  prev() {
    const track = this.trackTarget
    if (!track) return
    this.cardWidth = this.cardWidthCalc()
    const step = this.getItemsPerView()
    track.scrollBy({ left: -this.cardWidth * step, behavior: "smooth" })
  }

  getItemsPerView() {
    const width = window.innerWidth
    if (width < 768) return 1
    if (width < 1024) return 2
    return 3
  }

  dragStart(e) {
    const track = this.trackTarget
    if (!track) return
    this.isDragging = true
    this.axisLocked = null
    this.startX = e.pageX || (e.touches && e.touches[0].pageX) || 0
    this.startY = (e.touches && e.touches[0].pageY) || 0
    this.scrollLeft = track.scrollLeft
    track.style.cursor = "grabbing"
    track.style.scrollBehavior = "auto"
  }

  dragMove(e) {
    if (!this.isDragging) return
    const track = this.trackTarget
    if (!track) return

    const currentX = e.pageX || (e.touches && e.touches[0].pageX) || 0
    const currentY = (e.touches && e.touches[0].pageY) || 0
    const deltaX = Math.abs(currentX - this.startX)
    const deltaY = Math.abs(currentY - this.startY)

    if (this.axisLocked === null && (deltaX > 8 || deltaY > 8)) {
      this.axisLocked = deltaX > deltaY ? "horizontal" : "vertical"
    }

    if (this.axisLocked === "vertical") {
      this.isDragging = false
      track.style.cursor = ""
      track.style.scrollBehavior = ""
      return
    }

    if (e.cancelable) e.preventDefault()
    const walk = (currentX - this.startX) * 1.5
    track.scrollLeft = this.scrollLeft - walk
  }

  dragEnd() {
    this.isDragging = false
    this.axisLocked = null
    const track = this.trackTarget
    if (track) {
      track.style.cursor = ""
      track.style.scrollBehavior = ""
    }
  }

  removeDragListeners() {
    this.isDragging = false
    this.axisLocked = null
  }
}
